export interface MockEpisode {
  id: string;
  slug?: string;
  podcastName: string;
  podcastArtwork: string;
  episodeTitle: string;
  episodeUrl: string;
  audioUrl?: string;
  guestName?: string;
  publishDate: string;
  durationSeconds: number;
  confidenceScore: number;
  summary: string;
  whyChosen: string;
  relevantDiscussion: string;
  topics: string[];
  hasTranscript: boolean;
  artworkUrl?: string;
  spotifyUrl?: string;
  applePodcastsUrl?: string;
  spotifyEpisodeUrl?: string;
  appleEpisodeUrl?: string;
  bestStartSeconds?: number | null;
}

export const MOCK_RESULTS: Record<string, MockEpisode[]> = {
  default: [
    {
      id: "ep-001",
      podcastName: "Honest Ecommerce",
      podcastArtwork: "",
      episodeTitle: "How to Build a TikTok Ads Strategy That Actually Converts",
      episodeUrl: "https://example.com/ep1",
      guestName: "Nik Sharma",
      publishDate: "2025-11-14",
      durationSeconds: 3420,
      confidenceScore: 97,
      summary: "Nik Sharma breaks down his exact TikTok ad creative framework — from hook patterns to UGC briefs — and shares real spend data from 7-figure DTC brands.",
      whyChosen: "This episode directly addresses TikTok ad strategy with step-by-step tactical advice from one of the most respected DTC operators. The transcript contains detailed breakdowns of creative frameworks and budget allocation.",
      relevantDiscussion: "Between minutes 12-28, Nik walks through the 'hook-story-offer' framework and explains why static images are outperforming video on TikTok for certain product categories. He shares specific CPM benchmarks.",
      topics: ["TikTok Ads", "Creative Strategy", "DTC", "Paid Social", "UGC"],
      hasTranscript: true,
    },
    {
      id: "ep-002",
      podcastName: "Limited Supply",
      podcastArtwork: "",
      episodeTitle: "TikTok Shop Is Eating Shopify's Lunch — Here's What's Actually Happening",
      episodeUrl: "https://example.com/ep2",
      guestName: "Moiz Ali",
      publishDate: "2025-10-22",
      durationSeconds: 2880,
      confidenceScore: 89,
      summary: "Moiz Ali and Nik Sharma debate whether TikTok Shop is a real channel or a distraction. They compare conversion data across platforms and predict where social commerce is headed.",
      whyChosen: "Strong coverage of TikTok as a commerce platform with comparative data against other paid channels. Less tactical than the top result but excellent strategic context.",
      relevantDiscussion: "Around minute 18, they compare TikTok Shop AOV vs. Shopify checkout for the same products and discuss the implications for ad strategy.",
      topics: ["TikTok Shop", "Social Commerce", "Shopify", "DTC Strategy"],
      hasTranscript: true,
    },
    {
      id: "ep-003",
      podcastName: "Operators Podcast",
      podcastArtwork: "",
      episodeTitle: "We Spent $2M on TikTok Ads Last Quarter — Here's What We Learned",
      episodeUrl: "https://example.com/ep3",
      publishDate: "2025-09-05",
      durationSeconds: 4200,
      confidenceScore: 84,
      summary: "The Operators crew shares raw data from $2M+ in TikTok ad spend, including what creative types worked, what flopped, and how they structure testing.",
      whyChosen: "Real spend data and testing methodology from operators managing large budgets. Highly relevant for anyone running or planning TikTok ad campaigns.",
      relevantDiscussion: "Minutes 22-40 cover their creative testing framework: how they source UGC, structure A/B tests, and evaluate performance within 48 hours.",
      topics: ["TikTok Ads", "Ad Spend Data", "Creative Testing", "Scaling"],
      hasTranscript: false,
    },
  ],
  subscriptions: [
    {
      id: "ep-004",
      podcastName: "Subscription Radio",
      podcastArtwork: "",
      episodeTitle: "Building a Subscription Program That Doesn't Feel Like a Trap",
      episodeUrl: "https://example.com/ep4",
      guestName: "Matthew Holman",
      publishDate: "2025-12-01",
      durationSeconds: 3600,
      confidenceScore: 95,
      summary: "Matthew Holman explains how to structure subscription offers that customers actually want — covering pricing psychology, flexibility, and reducing churn from day one.",
      whyChosen: "The most comprehensive tactical episode on Shopify subscriptions. Deep discussion of pricing models, app selection, and retention mechanics.",
      relevantDiscussion: "Minutes 8-25 cover the 'subscribe and save vs. curated box' decision framework and when each model works best for different product categories.",
      topics: ["Subscriptions", "Shopify", "Retention", "Pricing", "Churn"],
      hasTranscript: true,
    },
    {
      id: "ep-005",
      podcastName: "DTC Pod",
      podcastArtwork: "",
      episodeTitle: "Why 80% of Subscription Programs Fail (And How to Fix Yours)",
      episodeUrl: "https://example.com/ep5",
      guestName: "Gabriella Feingold",
      publishDate: "2025-08-18",
      durationSeconds: 2700,
      confidenceScore: 88,
      summary: "A deep dive into common subscription program failures — from unclear value props to inflexible billing — with actionable fixes for each.",
      whyChosen: "Covers the failure modes most operators miss. Excellent complement to the top result with specific turnaround case studies.",
      relevantDiscussion: "The guest shares a case study around minute 15 where switching from fixed to flexible subscription intervals increased retention by 34%.",
      topics: ["Subscriptions", "DTC", "Churn Reduction", "Customer Experience"],
      hasTranscript: true,
    },
    {
      id: "ep-006",
      podcastName: "Honest Ecommerce",
      podcastArtwork: "",
      episodeTitle: "The Recharge Playbook: Advanced Subscription Tactics for Shopify",
      episodeUrl: "https://example.com/ep6",
      publishDate: "2025-07-10",
      durationSeconds: 3000,
      confidenceScore: 81,
      summary: "A Recharge PM walks through advanced features most brands don't use — including bundling, build-a-box, and churn prevention flows.",
      whyChosen: "Highly specific to Shopify subscription implementation. Great for operators ready to optimize an existing program.",
      relevantDiscussion: "Minutes 20-35 cover building a churn prevention flow using Recharge's cancellation survey data to route customers to the right retention offer.",
      topics: ["Subscriptions", "Shopify", "Recharge", "Retention Flows"],
      hasTranscript: true,
    },
  ],
};

export const EXAMPLE_PROMPTS = [
  "How do I increase Shopify conversion rate?",
  "What's the best way to reduce CAC for DTC brands?",
  "How should I structure post-purchase email flows?",
  "When should I use subscriptions vs one-time purchase?",
  "How do top brands approach influencer marketing?",
  "What's working right now for Meta ad creatives?",
  "How do I scale Amazon PPC profitably?",
  "What retention tactics actually move LTV for DTC?",
  "How do 8-figure brands structure their tech stack?",
  "What's the best post-iOS14 attribution setup?",
  "How do I price a subscription product?",
  "What's working for TikTok Shop right now?",
  "How do top operators run creative testing on Meta?",
  "When does retail expansion make sense for a DTC brand?",
  "How should I think about SMS vs email for retention?",
];

export const FEATURED_TOPICS = [
  "TikTok Ads",
  "Shopify",
  "CRO",
  "Email & SMS",
  "Amazon",
  "Retention",
  "Subscriptions",
  "Meta Ads",
];

export const LOADING_MESSAGES = [
  "Searching across top ecommerce podcasts…",
  "Digging through episodes…",
  "Checking transcripts…",
  "Looking for the smartest answer…",
  "Listening for tactical advice…",
  "Comparing the strongest matches…",
  "Ranking the best episodes for you…",
];

export const LOADING_PODCASTS = [
  "Honest Ecommerce",
  "Limited Supply",
  "Operators Podcast",
  "DTC Pod",
  "Subscription Radio",
  "eCommerce Fuel",
  "Chew On This",
  "The Jason & Scot Show",
  "My Wife Quit Her Job",
  "2X eCommerce",
];

export const VAGUE_QUERIES = [
  "how do i grow my store",
  "help me with marketing",
  "what should i do",
  "how do i sell more",
  "grow my business",
  "ecommerce tips",
];

export const CLARIFYING_QUESTIONS: Record<string, string[]> = {
  "how do i grow my store": [
    "Are you looking for paid acquisition tactics?",
    "More interested in retention and repeat purchases?",
    "Want to learn about organic growth and SEO?",
  ],
  "help me with marketing": [
    "Paid ads (Meta, TikTok, Google)?",
    "Email and SMS marketing?",
    "Content and organic growth?",
  ],
  default: [
    "Looking for paid advertising strategies?",
    "Interested in retention and loyalty?",
    "Want to improve conversion rate?",
    "Focused on a specific platform (Shopify, Amazon)?",
  ],
};

export function getResultsForQuery(query: string): MockEpisode[] {
  const q = query.toLowerCase();
  if (q.includes("subscription")) return MOCK_RESULTS.subscriptions;
  return MOCK_RESULTS.default;
}

export function isVagueQuery(query: string): boolean {
  const q = query.toLowerCase().trim();
  return VAGUE_QUERIES.some(v => q.includes(v) || v.includes(q)) || q.split(" ").length <= 3;
}

export function getClarifyingOptions(query: string): string[] {
  const q = query.toLowerCase().trim();
  for (const [key, options] of Object.entries(CLARIFYING_QUESTIONS)) {
    if (q.includes(key)) return options;
  }
  return CLARIFYING_QUESTIONS.default;
}
